import React, {useState, useContext, useMemo, useCallback} from 'react';
import OdxAreaHeader from '../../../OdxComponents/NativeOdx/Odx-AreaHeader';
import OdxTable, {TableHeaderCell} from '../../../OdxComponents/NativeOdx/Table/Odx-Table';
import {OdxTableRow} from '../../../OdxComponents/NativeOdx/Table/Odx-TableRow';
import {OdxTableCell} from '../../../OdxComponents/NativeOdx/Table/Odx-Tablecell';
import {odxIconSets} from '../../../OdxComponents/constants';
import {ISecret, IRepo} from "../Interfaces/IGithub";
import SecretSidesheet from "./Components/SecretSidesheet";
import {generateConsistentColor, getTextColor, PillStyle} from "./Components/ColorUtils";
import OdxBreadcrumbs from "../../../OdxComponents/NativeOdx/Odx-Breadcrumbs";
import {GithubDataContext} from "../githubDataContext";
import {badgeStyle} from "./Styles/styles";
import OdxSearchbar from "../../../OdxComponents/NativeOdx/Odx-Searchbar";
import OdxMainFilterGroup from "../../../OdxComponents/NativeOdx/Odx-MainFilterGroup";
import {formatDate, simplifyForHtmlId} from "../../../Utils/transformer";
import {useModuleViewTracking} from "../../../Utils/sentryTracking";

const HEADERS: TableHeaderCell[] = [
    {name: 'name', title: 'Name', sortable: true, required: true},
    {name: 'updated', title: 'Last updated', sortable: true},
    {name: 'repos', title: 'Linked Repositories', sortable: false}
];

const Secrets: React.FC = () => {
    const {repos, secrets, refreshData, org, slug, loading} = useContext(GithubDataContext);
    const [openSecret, setOpenSecret] = useState<ISecret | null>(null);
    const [sideOpen, setSideOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const baseId = 'github-secrets';

    useModuleViewTracking('github-secrets')

    const openEdit = useCallback(async (secret: ISecret) => {
        setOpenSecret(secret);
        setSideOpen(true);
    }, []);
    const openAdd = useCallback(() => {
        setOpenSecret(null);
        setSideOpen(true);
    }, []);

    const filteredSecrets = useMemo(() => {
        if (!searchText.trim()) return secrets;
        return secrets.filter(secret =>
            secret.name.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [secrets, searchText]);

    const secretRows = useMemo(() =>
        filteredSecrets.map((s) => {
            const secretKey = simplifyForHtmlId(s.name);
            return (
                <OdxTableRow
                    key={s.name}
                    id={`${baseId}-row-${secretKey}`}
                    onClick={() => openEdit(s)}
                >
                    <OdxTableCell className={"cursor-pointer"} id={`${baseId}-row-${secretKey}-name`}>
                        <div className="flex items-center">
                            <div
                                className="w-[5px] h-full !mr-2 min-h-[24px]"
                                style={{
                                    backgroundColor: generateConsistentColor(s.name)
                                }}
                            />
                            {s.name}
                        </div>
                    </OdxTableCell>
                    <OdxTableCell className={"cursor-pointer"}
                                  id={`${baseId}-row-${secretKey}-updated`}>{formatDate(s.updated_at)}</OdxTableCell>
                    <OdxTableCell className={"cursor-pointer"} id={`${baseId}-row-${secretKey}-repos`}>
                        {s.selectedRepos && s.selectedRepos.length
                            ? (
                                <div id={`${baseId}-row-${secretKey}-repos-list`}
                                     style={{display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
                                    {s.selectedRepos.slice(0, 3).map(repo => (
                                        <div
                                            key={repo.id}
                                            id={`${baseId}-row-${secretKey}-repo-${simplifyForHtmlId(repo.name)}`}
                                            style={{
                                                ...PillStyle,
                                                backgroundColor: generateConsistentColor(repo.name),
                                                color: getTextColor(generateConsistentColor(repo.name))
                                            }}>
                                            {repo.name}
                                        </div>
                                    ))}
                                    {s.selectedRepos.length > 3 && (
                                        <div
                                            id={`${baseId}-row-${secretKey}-repos-more`}
                                            style={{alignSelf: 'center', fontWeight: 500, marginLeft: 4}}>
                                            +{s.selectedRepos.length - 3} more
                                        </div>
                                    )}
                                </div>
                            )
                            : <span id={`${baseId}-row-${secretKey}-repos-empty`}>No linked repositories</span>
                        }
                    </OdxTableCell>
                </OdxTableRow>
            );
        }),
        [filteredSecrets, openEdit]);

    return (
        <div id={`${baseId}-view`}>
            <OdxBreadcrumbs id={`${baseId}-breadcrumbs`}/>
            <OdxAreaHeader
                id={`${baseId}-header`}
                title="GitHub secrets"
                text="GitHub Secrets"
                className={'!pl-0 !pr-0'}
            />
            <OdxMainFilterGroup id={`${baseId}-filters`}>
                <div className="flex-grow" id={`${baseId}-search-container`}>
                    <OdxSearchbar
                        onSearch={setSearchText}
                        id={`${baseId}-search`}
                    />
                </div>
                <div id={`${baseId}-count`}>
                    <span id={`${baseId}-count-label`}>GitHub secrets</span>
                    <span id={`${baseId}-count-badge`} style={badgeStyle}>{filteredSecrets.length}</span>
                    <odx-button
                        variant="primary"
                        size="lg"
                        id={`${baseId}-add-btn`}
                        className="!mt-0 !mb-0 !ml-8"
                        type="button"
                        onClick={openAdd}
                    >
                        <odx-icon slot="prefix" size="md" name={`${odxIconSets.core}::plus`}></odx-icon>
                        Add
                    </odx-button>
                </div>
            </OdxMainFilterGroup>
            <div className={"!pt-5 overflow-x-auto"} id={`${baseId}-table-wrapper`}>
                <OdxTable headerData={HEADERS} className={"w-full"} id={`${baseId}-table`}>
                    {filteredSecrets.length === 0
                        ? (<OdxTableRow id={`${baseId}-empty-row`}>
                            <OdxTableCell colSpan={HEADERS.length}
                                          style={{textAlign: 'center'}}
                                          id={`${baseId}-empty-cell`}>No
                                secrets found.</OdxTableCell></OdxTableRow>)
                        : secretRows}
                </OdxTable>
            </div>
            {sideOpen && <SecretSidesheet
                id={`${baseId}-sidesheet`}
                secret={openSecret}
                isOpen={sideOpen}
                onClose={() => setSideOpen(false)}
                onSaved={refreshData}
                org={org}
                slug={slug}
                allRepos={repos}
            />}
        </div>
    );
};

export default Secrets;
